/**
 * MapSnapshotService - Servicio para gestión de vistas guardadas del mapa
 *
 * @version 1.0
 * @updated 2026-02-19
 * @module core/map-engine
 *
 * @description
 * Servicio para guardar, listar y restaurar "snapshots" del estado del mapa.
 * Un snapshot incluye: centro, zoom, bearing, pitch, capas visibles y fecha.
 *
 * @remarks
 * **Características**:
 * - Guardar estado completo del mapa
 * - Persistencia en LocalStorage
 * - Restaurar vistas guardadas
 * - Exportar snapshots con captura de imagen
 * - Gestión de galería (listar, eliminar, renombrar)
 *
 * @example
 * ```typescript
 * import { mapSnapshotService } from '@/core/map-engine/MapSnapshotService';
 *
 * // Guardar snapshot actual
 * const snapshot = await mapSnapshotService.saveSnapshot({
 *   name: 'Vista del centro histórico',
 *   description: 'Forestación en barrio centro'
 * });
 *
 * // Listar snapshots guardados
 * const snapshots = mapSnapshotService.listSnapshots();
 *
 * // Restaurar snapshot
 * await mapSnapshotService.restoreSnapshot(snapshot.id);
 * ```
 */

import type { Map as MapLibreMap } from 'maplibre-gl';

/**
 * Estado del mapa en un momento dado
 */
export interface MapSnapshot {
  /**
   * ID único del snapshot
   */
  id: string;

  /**
   * Nombre del snapshot (editable por usuario)
   */
  name: string;

  /**
   * Descripción opcional
   */
  description?: string;

  /**
   * Estado del mapa
   */
  mapState: {
    center: [number, number];
    zoom: number;
    bearing: number;
    pitch: number;
  };

  /**
   * Capas visibles al momento del snapshot
   */
  visibleLayers?: string[];

  /**
   * Thumbnail (base64 data URL)
   */
  thumbnail?: string;

  /**
   * Fecha de creación
   */
  createdAt: Date;

  /**
   * Fecha de última modificación
   */
  updatedAt: Date;

  /**
   * ID del proyecto asociado
   */
  projectId?: string;
}

/**
 * Opciones para crear snapshot
 */
export interface CreateSnapshotOptions {
  /**
   * Nombre del snapshot
   */
  name: string;

  /**
   * Descripción opcional
   */
  description?: string;

  /**
   * Generar thumbnail
   * @default true
   */
  generateThumbnail?: boolean;

  /**
   * ID del proyecto (opcional)
   */
  projectId?: string;
}

/**
 * Servicio de gestión de snapshots del mapa
 */
export class MapSnapshotService {
  private readonly STORAGE_KEY = 'westeer_map_snapshots';
  private map: MapLibreMap | null = null;

  /**
   * Registra la instancia del mapa
   */
  setMap(map: MapLibreMap): void {
    this.map = map;
    console.log('[MapSnapshotService] Mapa registrado');
  }

  /**
   * Obtiene la instancia del mapa
   */
  getMap(): MapLibreMap | null {
    return this.map;
  }

  /**
   * Guarda un snapshot del estado actual del mapa
   */
  async saveSnapshot(options: CreateSnapshotOptions): Promise<MapSnapshot> {
    const { name, description, generateThumbnail = true, projectId } = options;

    if (!this.map) {
      throw new Error('[MapSnapshotService] No map instance available');
    }

    console.log('[MapSnapshotService] Guardando snapshot:', name);

    // Capturar estado del mapa
    const center = this.map.getCenter();
    const mapState = {
      center: [center.lng, center.lat] as [number, number],
      zoom: this.map.getZoom(),
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch(),
    };

    // Obtener capas visibles (TODO: implementar lógica real)
    const visibleLayers: string[] = [];

    // Generar thumbnail
    let thumbnail: string | undefined;
    if (generateThumbnail) {
      try {
        thumbnail = this.generateThumbnail();
      } catch (error) {
        console.warn('[MapSnapshotService] No se pudo generar thumbnail:', error);
      }
    }

    // Crear snapshot
    const snapshot: MapSnapshot = {
      id: this.generateId(),
      name,
      description,
      mapState,
      visibleLayers,
      thumbnail,
      createdAt: new Date(),
      updatedAt: new Date(),
      projectId,
    };

    // Guardar en LocalStorage
    this.addSnapshotToStorage(snapshot);

    console.log('[MapSnapshotService] Snapshot guardado:', snapshot.id);

    return snapshot;
  }

  /**
   * Restaura un snapshot al mapa
   */
  async restoreSnapshot(snapshotId: string): Promise<void> {
    const snapshot = this.getSnapshot(snapshotId);

    if (!snapshot) {
      throw new Error(`[MapSnapshotService] Snapshot not found: ${snapshotId}`);
    }

    if (!this.map) {
      throw new Error('[MapSnapshotService] No map instance available');
    }

    console.log('[MapSnapshotService] Restaurando snapshot:', snapshot.name);

    // Animar transición al nuevo estado
    this.map.flyTo({
      center: snapshot.mapState.center,
      zoom: snapshot.mapState.zoom,
      bearing: snapshot.mapState.bearing,
      pitch: snapshot.mapState.pitch,
      duration: 1500,
    });

    // TODO: Restaurar capas visibles
    // snapshot.visibleLayers?.forEach(layerId => {
    //   map.setLayoutProperty(layerId, 'visibility', 'visible');
    // });

    console.log('[MapSnapshotService] Snapshot restaurado exitosamente');
  }

  /**
   * Lista todos los snapshots guardados
   */
  listSnapshots(projectId?: string): MapSnapshot[] {
    const allSnapshots = this.getSnapshotsFromStorage();

    // Filtrar por proyecto si se especifica
    if (projectId) {
      return allSnapshots.filter((s) => s.projectId === projectId);
    }

    return allSnapshots;
  }

  /**
   * Obtiene un snapshot por ID
   */
  getSnapshot(snapshotId: string): MapSnapshot | null {
    const snapshots = this.getSnapshotsFromStorage();
    return snapshots.find((s) => s.id === snapshotId) || null;
  }

  /**
   * Elimina un snapshot
   */
  deleteSnapshot(snapshotId: string): void {
    const snapshots = this.getSnapshotsFromStorage();
    const filtered = snapshots.filter((s) => s.id !== snapshotId);

    this.saveSnapshotsToStorage(filtered);

    console.log('[MapSnapshotService] Snapshot eliminado:', snapshotId);
  }

  /**
   * Actualiza un snapshot (nombre, descripción)
   */
  updateSnapshot(snapshotId: string, updates: Partial<Pick<MapSnapshot, 'name' | 'description'>>): void {
    const snapshots = this.getSnapshotsFromStorage();
    const index = snapshots.findIndex((s) => s.id === snapshotId);

    if (index === -1) {
      throw new Error(`[MapSnapshotService] Snapshot not found: ${snapshotId}`);
    }

    snapshots[index] = {
      ...snapshots[index],
      ...updates,
      updatedAt: new Date(),
    };

    this.saveSnapshotsToStorage(snapshots);

    console.log('[MapSnapshotService] Snapshot actualizado:', snapshotId);
  }

  /**
   * Genera un thumbnail del mapa actual
   * @private
   */
  private generateThumbnail(): string {
    if (!this.map) {
      throw new Error('No map instance');
    }

    const canvas = this.map.getCanvas();
    return canvas.toDataURL('image/jpeg', 0.5); // Calidad 50% para reducir tamaño
  }

  /**
   * Obtiene snapshots desde LocalStorage
   * @private
   */
  private getSnapshotsFromStorage(): MapSnapshot[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) return [];

      const parsed = JSON.parse(data);

      // Convertir strings de fecha a objetos Date
      return parsed.map((s: any) => ({
        ...s,
        createdAt: new Date(s.createdAt),
        updatedAt: new Date(s.updatedAt),
      }));
    } catch (error) {
      console.error('[MapSnapshotService] Error al leer snapshots:', error);
      return [];
    }
  }

  /**
   * Guarda snapshots en LocalStorage
   * @private
   */
  private saveSnapshotsToStorage(snapshots: MapSnapshot[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(snapshots));
    } catch (error) {
      console.error('[MapSnapshotService] Error al guardar snapshots:', error);
      throw error;
    }
  }

  /**
   * Agrega un snapshot al storage
   * @private
   */
  private addSnapshotToStorage(snapshot: MapSnapshot): void {
    const snapshots = this.getSnapshotsFromStorage();
    snapshots.push(snapshot);
    this.saveSnapshotsToStorage(snapshots);
  }

  /**
   * Genera un ID único
   * @private
   */
  private generateId(): string {
    return `snapshot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Limpia todos los snapshots (usar con precaución)
   */
  clearAllSnapshots(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    console.log('[MapSnapshotService] Todos los snapshots eliminados');
  }

  /**
   * Exporta snapshots como JSON
   */
  exportSnapshots(): string {
    const snapshots = this.getSnapshotsFromStorage();
    return JSON.stringify(snapshots, null, 2);
  }

  /**
   * Importa snapshots desde JSON
   */
  importSnapshots(json: string): void {
    try {
      const snapshots = JSON.parse(json);
      this.saveSnapshotsToStorage(snapshots);
      console.log('[MapSnapshotService] Snapshots importados exitosamente');
    } catch (error) {
      console.error('[MapSnapshotService] Error al importar snapshots:', error);
      throw error;
    }
  }
}

/**
 * Instancia singleton del servicio
 */
export const mapSnapshotService = new MapSnapshotService();
